import { Locale } from '../i18n'

export interface DateHook {
  formatDate: (input?: string | null) => string
  formatUnixDate: (input: number | Date) => string
  nextDay: (input: string, days?: number) => string
  getLocalFormat: () => string
  getPart: (input: number | Date, type: Intl.DateTimeFormatPartTypes) => string
}

export interface DateTimeFormatOptions {
  weekday?: 'narrow' | 'short' | 'long'
  era?: 'narrow' | 'short' | 'long'
  year?: 'numeric' | '2-digit'
  month?: 'numeric' | '2-digit' | 'narrow' | 'short' | 'long'
  day?: 'numeric' | '2-digit'
  hour?: 'numeric' | '2-digit'
  minute?: 'numeric' | '2-digit'
  second?: 'numeric' | '2-digit'
  timeZoneName?: 'short' | 'long'
  timeZone?: string
  hour12?: boolean
  formatMatcher?: 'basic' | 'best fit'
}

export interface DateConfig extends DateTimeFormatOptions {
  locale?: Locale | 'default'
}

export const useDate = ({ locale = 'default', ...options }: DateConfig): DateHook => {
  const formattingOptions: DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    ...options,
  }

  // @TODO: Temporary work around to fix Safari iOS 15 date formatting issue for en-AU locale:
  // CHECKOUT-566: https://github.com/AfterpayTouch/rocketship/pull/1203
  const tempLocale = locale === 'en-AU' && formattingOptions.year === 'numeric' && formattingOptions.month === '2-digit' ? 'en-NZ' : locale

  const formatter = new Intl.DateTimeFormat(tempLocale, formattingOptions)

  const formatDate = (input?: string | null): string => {
    if (input === null || input === undefined) {
      return ''
    }
    // Safari doesn't support - date separator and instead it looks for / separator
    const transformedInput = input.replace(/-/g, '/')
    return formatter.format(new Date(transformedInput))
  }

  const formatUnixDate = (input: number | Date): string => {
    if (input === null || input === undefined) {
      return ''
    }
    return formatter.format(new Date(input))
  }

  const nextDay = (input: string, days: number = 1): string => {
    if (input === null || input === undefined) {
      return ''
    }
    // Safari doesn't support - date separator and instead it looks for / separator
    const transformedInput = input.replace(/-/g, '/')
    const dateInput = new Date(transformedInput)
    const targetDate = dateInput.setDate(dateInput.getDate() + days)

    return formatter.format(targetDate)
  }

  const getLocalFormat = (): string => {
    const year = 2222
    const month = 12
    const day = 15
    const formattedDate = formatter.format(new Date(year, month - 1, day))
    return formattedDate.replace(`${year}`, 'YYYY').replace(`${month}`, 'MM').replace(`${day}`, 'DD')
  }

  const getPart = (input: number | Date, type: Intl.DateTimeFormatPartTypes): string => {
    const parts = formatter.formatToParts(new Date(input))
    return parts.find((p) => p.type === type)?.value ?? ''
  }

  return { formatDate, formatUnixDate, nextDay, getLocalFormat, getPart }
}
